import { useEffect, useRef, useState } from 'react'
import { STUDY_STATUS_LABEL, STATUS_FINISHED_OPTIONS } from '../../constants'
import {
  CustomTable,
  CustomTableRef,
  stringFilterTemplate,
  DateFilterTemplate,
  ButtonOverlay,
  TableButton,
} from 'medere-ui-framework'
import { EStudyInformStatus } from '../../enums'
import {
  getAllStudiesInform,
  getStudyTypes,
  validateStudyInform,
  exportStudiesInform,
} from '../../../../services/StudiesInformController'
import { getProfessionalData } from '../../../../servicesHooks/DigitalRP/useDigitalRPService'
import { StudiesInformListItemDTO } from '../../../../dto/StudiesInformListItemDTO'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { Dropdown } from 'primereact/dropdown'
import { ColumnFilterElementTemplateOptions } from 'primereact/column'
import StudiesInformCrudPanel from '../../StudiesInformCrudPanel'
import { formatDateWithTime } from '../../helpers'
import StudyInformPdfView from './StudyInformPdfView'

function FinishedCard({ entity, btn1, btn2 }: { entity: StudiesInformListItemDTO; btn1?: any; btn2?: any }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem' }}>
      <div>
        <strong>{entity.patientFullName}</strong>
        <p style={{ margin: 0 }}>{entity.studyTypeLabel} — {entity.requestedDate}</p>
        <p style={{ margin: 0, color: '#888' }}>{entity.professionalFullName} | {entity.status}</p>
      </div>
      <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
        {btn1 && <Button className={btn1.className} icon={"pi " + btn1.icon} onClick={btn1.onClick} />}
        {btn2 && <Button className={btn2.className} icon={"pi " + btn2.icon} onClick={btn2.onClick} />}
      </div>
    </div>
  )
}

interface FinishedViewProps {
  onlyOwnProfessional?: boolean
}

export default function FinishedView({ onlyOwnProfessional = false }: Readonly<FinishedViewProps>) {
  const tableRef = useRef<CustomTableRef>(null)
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null)
  const [selectedEntityMode, setSelectedEntityMode] = useState<'view' | 'edit'>('view')
  const [typeFilter, setTypeFilter] = useState<string | null>(null)
  const [typeOptions, setTypeOptions] = useState<Array<{ label: string; value: string }>>([])
  const [statusFilter, setStatusFilter] = useState<EStudyInformStatus | null>(null)
  const [pdfTargetId, setPdfTargetId] = useState<number | null>(null)
  const [ownProfessionalId, setOwnProfessionalId] = useState<number | null>(null)

  useEffect(() => {
    getStudyTypes().then(setTypeOptions).catch(() => setTypeOptions([]))
  }, [])

  useEffect(() => {
    if (!onlyOwnProfessional) return
    getProfessionalData()
      .then((professional) => setOwnProfessionalId(professional?.professionaid ?? null))
      .catch(() => setOwnProfessionalId(null))
  }, [onlyOwnProfessional])

  function openView(item: StudiesInformListItemDTO, mode: String) {
    setSelectedEntityMode(item.status == 'SD_REQUESTED' || mode == 'edit' ? 'edit' : 'view');
    setSelectedEntityId(item.studyDiagnosticId)
  }

  function handleValidate(id: number) {
    validateStudyInform(id)
      .then(() => {
        setSelectedEntityId(null)
        tableRef.current?.refreshTable?.()
      })
  }

  if (pdfTargetId != null) {
    return (
      <StudyInformPdfView
        studyDiagnosticId={pdfTargetId}
        onClose={() => setPdfTargetId(null)}
      />
    )
  }

  if (selectedEntityId != null) {
    return (
      <StudiesInformCrudPanel
        StudyInformId={selectedEntityId}
        setStudyInformId={setSelectedEntityId}
        mode={selectedEntityMode}
        setMode={setSelectedEntityMode}
        onSaved={() => tableRef.current?.refreshTable?.()}
        onShowPdf={() => setPdfTargetId(selectedEntityId)}
        onValidate={() => { handleValidate(selectedEntityId!) }}
      />
    )
  }

  return (
    <div className='viewContainer studies-inform-view'>
      <CustomTable
        idTable='StudiesInformFinishedTable'
        className='studies-inform-table'
        ref={tableRef}
        getItems={getAllStudiesInform}
        params={{
          studyInformStatus: statusFilter ?? [EStudyInformStatus.SD_INFORMED_COMPLETE, EStudyInformStatus.SD_INFORMED_PARTIAL, EStudyInformStatus.SD_VALIDATED],
          typeFilter,
          professionalId: onlyOwnProfessional ? ownProfessionalId : null,
        }}
        resizableColumns
        dynamicBehaviour
        CardTemplate={FinishedCard}
        cardKeyName='studyDiagnosticId'
        filterRow
        hideFiltersButton
        headerClassName='studies-inform-header'
        refreshButton
        exportItems={[{ label: 'Excel', icon: 'pi-file-excel', onExport: exportStudiesInform }]}
        onClearFilters={() => { setTypeFilter(null); setStatusFilter(null) }}
      >
        <Column
          field='patientFullName'
          className='column'
          header='Paciente'
          filter
          filterElement={stringFilterTemplate}
          body={(row: StudiesInformListItemDTO) => (
            <div>
              <div>{row.patientFullName}</div>
              <div style={{ fontSize: '0.8em', color: '#888' }}>{row.patientFullDoc}</div>
            </div>
          )}
        />
        <Column
          field='servicedatetime'
          className='column'
          header='Fecha y hora'
          filter
          filterElement={DateFilterTemplate}
          body={(row: StudiesInformListItemDTO) => formatDateWithTime(row.serviceDate)}
          style={{ minWidth: '13rem' }}
        />
        <Column
          field='studyTypeLabel'
          className='column'
          header='Estudio'
          filter
          filterElement={(filter: ColumnFilterElementTemplateOptions) => (
            <Dropdown
              options={typeOptions}
              optionLabel='label'
              optionValue='value'
              value={typeFilter}
              showClear
              className='w-full'
              style={{ height: '2.25rem', marginBottom: '0.28rem' }}
              onChange={(e) => {
                const newValue = (e.value ?? null) as string | null
                setTypeFilter(newValue)
                filter.filterCallback(newValue)
              }}
            />
          )}
          style={{ minWidth: '10rem' }}
        />
        <Column
          field='professionalFullName'
          className='column'
          header='Medico Solicitante'
          filter
          filterElement={stringFilterTemplate}
        />
        <Column
          field='status'
          className='column'
          header='Estado'
          filter
          filterElement={(filter: ColumnFilterElementTemplateOptions) => (
            <Dropdown
              options={STATUS_FINISHED_OPTIONS}
              optionLabel='label'
              optionValue='value'
              value={statusFilter}
              showClear
              className='w-full'
              style={{ height: '2.25rem', marginBottom: '0.28rem' }}
              onChange={(e) => {
                const newValue = (e.value ?? null) as EStudyInformStatus | null
                setStatusFilter(newValue)
                filter.filterCallback(newValue)
              }}
            />
          )}
          body={(row: StudiesInformListItemDTO) => ( STUDY_STATUS_LABEL[row.status] )}
        />

        <Column
          filter
          filterElement={() => (
            <Button
              icon='pi pi-eraser'
              className='buttonControl'
              tooltip='Limpiar filtros'
              tooltipOptions={{ position: 'top' }}
              onClick={() => {
                tableRef.current?.internalClearFilters?.()
                setTypeFilter(null)
                setStatusFilter(null)
              }}
            />
          )}
          body={(rowData: StudiesInformListItemDTO) => (
        <ButtonOverlay buttonClassName='button-grey p-button-rounded' >
          <div className='flex flex-column'>
            <TableButton
              label='Ver detalle'
              icon='pi-eye'
              iconPos='left'
              onClick={() => {
                openView(rowData, 'view')
              }}
              className='text-left TableButtonStyle text-sm my-1 p-1'
            />
            <TableButton
              label='PDF Informe'
              icon='pi-file'
              iconPos='left'
              onClick={() => setPdfTargetId(rowData.studyDiagnosticId)}
              className='text-left TableButtonStyle text-sm my-1 p-1'
            />
            { rowData.status == 'SD_INFORMED_PARTIAL' &&  
            <TableButton
              label='Completar'
              icon='pi-check-square'
              iconPos='left'
              onClick={() => {
                openView(rowData, 'edit')
              }}
              className='text-left TableButtonStyle text-sm my-1 p-1'
            />
            }           
          </div>
        </ButtonOverlay>
      )} />
      </CustomTable>
    </div>
  )
}
